import {useState} from "react";
import {Eye, Maximize2, Pen, Trash2} from "lucide-react";
import {Scene} from "@/scenes/Scene.ts";
import {Button} from "@/components/ui/button.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import DeleteConfirm from "@/components/forms/DeleteConfirm.tsx";
import AuditMetadata from "@/components/management/AuditMetadata.tsx";
import {srcUrl} from "@/api/ImageApi.ts";

export default function SceneDetail({scene, isDisplayed, onDisplay, onEdit, onDelete}: {
    scene: Scene;
    isDisplayed: boolean;
    onDisplay: () => void;
    onEdit: () => void;
    onDelete: () => Promise<void>;
}) {
    const [imageStatus, setImageStatus] = useState<"loading" | "loaded" | "error">("loading");
    const imageUrl = srcUrl(scene.image);

    return (
        <article className="flex size-full min-h-0 flex-col gap-3">
            <title>{`${scene.name} | DireDawn`}</title>
            <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="truncate text-xl font-semibold">{scene.name}</h1>
                        {isDisplayed && <Badge variant="secondary"><Eye/> Wird angezeigt</Badge>}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {scene.image.width} × {scene.image.height} px · {aspectRatio(scene.image.width, scene.image.height)}
                        {scene.image.fileName && ` · ${scene.image.fileName}`}
                    </p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button variant={isDisplayed ? "secondary" : "primary"} size="sm" onClick={onDisplay}>
                        <Eye/> Spielern anzeigen
                    </Button>
                    <Button variant="outline" size="sm" onClick={onEdit}><Pen/> Bearbeiten</Button>
                    <Button variant="outline" size="icon-sm" title="Bild in Originalgröße öffnen" asChild>
                        <a href={imageUrl} target="_blank" rel="noreferrer"><Maximize2/></a>
                    </Button>
                    <DeleteConfirm title={`${scene.name} löschen?`} onConfirm={onDelete}>
                        <Button size="icon-sm" variant="destructive" title="Löschen"
                                onSelect={(event) => event.preventDefault()}><Trash2/></Button>
                    </DeleteConfirm>
                </div>
            </header>
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg border bg-black/20 p-2">
                {imageStatus === "loading" && <p role="status" className="absolute text-sm text-muted-foreground">Szenenbild wird geladen…</p>}
                {imageStatus === "error" && (
                    <div className="text-center text-sm text-muted-foreground">
                        <p>Das Szenenbild konnte nicht geladen werden.</p>
                        <Button className="mt-2" variant="outline" size="sm" onClick={() => setImageStatus("loading")}>Erneut laden</Button>
                    </div>
                )}
                {imageStatus !== "error" && (
                    <img src={imageUrl} alt={`Szenenbild: ${scene.name}`} className="size-full object-contain"
                         onLoad={() => setImageStatus("loaded")} onError={() => setImageStatus("error")}/>
                )}
            </div>
            <footer className="shrink-0"><AuditMetadata createdAt={scene.createdAt} updatedAt={scene.updatedAt}/></footer>
        </article>
    );
}

function aspectRatio(width: number, height: number) {
    if (width <= 0 || height <= 0) return "unbekanntes Format";
    const divisor = greatestCommonDivisor(width, height);
    return `${width / divisor}:${height / divisor}`;
}

function greatestCommonDivisor(first: number, second: number): number {
    return second === 0 ? first : greatestCommonDivisor(second, first % second);
}
